import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { STRATEGIES, STRATEGY_MAPPINGS } from '../constants/strategies';
import SortButton from './SortButton';
import Pick, { PickListHeader } from './Pick';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getAuth } from 'firebase/auth';
import { formatExpectedReturn, formatDollarChange, toneOfSigned } from '../utils/returns';
import { formatEventDate, readApiError, savedPick } from '../utils/builder';
import AutoPicksOption from './AutoPicksOption';

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';

// Helper: invert STRATEGY_MAPPINGS for display
function getFriendlyStrategyName(columnName) {
  for (const [strategyName, options] of Object.entries(STRATEGY_MAPPINGS)) {
    for (const [option, col] of Object.entries(options)) {
      if (col === columnName) {
        return `${strategyName} (${option})`;
      }
    }
  }
  return columnName;
}

export default function SimpleBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selections, setSelections] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [playbookName, setPlaybookName] = useState('');
  const [autoPicks, setAutoPicks] = useState(true);
  const [sortField, setSortField] = useState('weight');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedSections, setExpandedSections] = useState({ strategyWeights: false, recommendedPicks: false });

  // Helper functions for displaying values
  const formatDate = formatEventDate;

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };


  const handleSelection = (option) => {
    setSelections({
      ...selections,
      [STRATEGIES[currentStep].name]: option
    });
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/playbooks/build');
    }
  };

  const handleNext = () => {
    if (currentStep < STRATEGIES.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const getStrategyColumns = () => {
    return Object.entries(selections).map(([strategyName, selectedOption]) => {
      return STRATEGY_MAPPINGS[strategyName][selectedOption];
    });
  };

  const handleBuildPlaybook = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const columns = getStrategyColumns();
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      // Step 1: Optimize
      const optimizeRes = await fetch(`${ODIKAI_API_BASE}/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          strategies: columns
        })
      });
      if (!optimizeRes.ok) {
        throw new Error(await readApiError(optimizeRes, 'Failed to optimize portfolio'));
      }
      const optimizeResponse = await optimizeRes.json();
      // Step 2: Get picks
      const picksRes = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portfolio: optimizeResponse.portfolio
        })
      });
      if (!picksRes.ok) {
        throw new Error(await readApiError(picksRes, 'Failed to get picks'));
      }
      const picksResponse = await picksRes.json();
      // Merge results for display
      setApiResponse({
        ...optimizeResponse,
        picks: picksResponse.picks,
        event_name: picksResponse.event_name,
        event_date: picksResponse.event_date,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveClick = () => {
    setPlaybookName(`Custom Playbook ${new Date().toLocaleDateString()}`);
    setShowNameDialog(true);
  };

  const handleSavePlaybook = async () => {
    if (!apiResponse || !user || !playbookName.trim()) return;

    try {
      const playbookId = `${user.uid}_${Date.now()}`;
      const playbookData = {
        is_prebuilt: false, // REQUIRED: Must be false for user-created playbooks
        user_id: user.uid, // REQUIRED: Must match authenticated user
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        name: playbookName.trim(),
        is_public: false, // Default to private
        creator_type: 'user',
        event_name: apiResponse.event_name,
        event_date: apiResponse.event_date,
        strategies: 10,
        selected_strategies: getStrategyColumns(),
        portfolio: apiResponse.portfolio.filter(item => item.strategy).map(item => ({
          strategy: item.strategy,
          weight: item.weight
        })),
        picks: apiResponse.picks.map(pick => savedPick(pick, apiResponse.event_name, apiResponse.event_date)),
        portfolio_returns: apiResponse.portfolio_returns,
        previous_year_returns: apiResponse.previous_year_returns,
        playbook_type: 'simple',
        auto_picks: autoPicks
      };

      await setDoc(doc(db, 'playbooks', playbookId), playbookData);
      navigate('/playbooks');
    } catch (err) {
      setError('Failed to save playbook: ' + err.message);
    }
  };

  const handleDiscard = () => {
    setApiResponse(null);
    setCurrentStep(0);
    setSelections({});
  };

  const currentStrategy = STRATEGIES[currentStep];
  const progress = ((currentStep + 1) / STRATEGIES.length) * 100;

  // Add sort handler
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  if (isLoading) {
    return (
      <div className="simple-builder-section">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <h2>Building your playbook…</h2>
          <p>We're analyzing your strategy preferences and creating your custom playbook.</p>
        </div>
      </div>
    );
  }

  if (apiResponse) {
    return (
      <div className="simple-builder-section">
        <div className="results-view">
          <div className="picks-display">
            <div className="section-header">
              <h2>Your custom playbook</h2>
              
              <div className="results-summary">
                <div className="summary-item">
                  <h3>Event</h3>
                  <p>{apiResponse.event_name || 'No event selected'}</p>
                  <p className="date">{formatDate(apiResponse.event_date)}</p>
                </div>
                <div className="summary-item">
                  <h3>Expected yearly return</h3>
                  <p className={toneOfSigned(apiResponse.portfolio_returns)}>{formatExpectedReturn(apiResponse.portfolio_returns)}</p>
                </div>
                <div className="summary-item">
                  <h3>Last year on $5,000</h3>
                  <p className={toneOfSigned(apiResponse.previous_year_returns)}>{formatDollarChange(apiResponse.previous_year_returns)}</p>
                </div>
              </div>
            </div>

            <div className="accordion-sections">
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleSection('strategyWeights')}
                >
                  <h3>Strategy weights</h3>
                  {expandedSections.strategyWeights ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.strategyWeights && (
                  <div className="accordion-content strategy-weights">
                    {apiResponse.portfolio
                      .filter(item => item.strategy)
                      .map((item, index) => (
                        <div key={index} className="strategy-weight-item">
                          <span className="strategy-name" title={item.strategy}>{getFriendlyStrategyName(item.strategy)}</span>
                          <span className="strategy-weight">{formatPercent(item.weight)}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleSection('recommendedPicks')}
                >
                  <h3>Recommended picks</h3>
                  {expandedSections.recommendedPicks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.recommendedPicks && (
                  <div className="accordion-content">
                    <div className="sort-controls">
                      <SortButton 
                        field="weight" 
                        label="Sort by Weight" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                      <SortButton 
                        field="odds" 
                        label="Sort by Odds" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                      <SortButton 
                        field="strategies" 
                        label="Sort by Support" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                    </div>

                    {apiResponse.picks.length === 0 && (
                      <p className="empty-note">None of your strategies make a pick on this card yet.</p>
                    )}

                    <PickListHeader />

                    {[...apiResponse.picks]
                      .sort((a, b) => {
                        let aValue, bValue;
                        switch (sortField) {
                          case 'weight':
                            aValue = a.augmented_weight;
                            bValue = b.augmented_weight;
                            break;
                          case 'odds':
                            aValue = parseFloat(a.odds);
                            bValue = parseFloat(b.odds);
                            break;
                          case 'strategies':
                            aValue = Array.isArray(a.strategy) ? a.strategy.length : (a.strategy ? 1 : 0);
                            bValue = Array.isArray(b.strategy) ? b.strategy.length : (b.strategy ? 1 : 0);
                            break;
                          default:
                            return 0;
                        }
                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                      })
                      .map((pick, index) => {
                        const supporting = Array.isArray(pick.strategy) ? pick.strategy.length : (pick.strategy ? 1 : 0);
                        return (
                          <Pick
                            key={index}
                            pick_name={pick.pick}
                            opponent_name={pick.opponent}
                            type={pick.pick_type}
                            weight={pick.augmented_weight}
                            american_odds={pick.odds_us}
                            decimal_odds={parseFloat(pick.odds)}
                            supporting_strategies={supporting}
                            total_strategies={apiResponse.portfolio.filter(item => item.strategy).length}
                            maxWeight={Math.max(0, ...apiResponse.picks.map(p => p.augmented_weight || 0))}
                          />
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="action-buttons">
            <button className="nav-button back-button" onClick={handleDiscard}>
              Discard
            </button>
            <button className="nav-button next-button" onClick={handleSaveClick}>
              Save playbook
            </button>
          </div>
        </div>

        {showNameDialog && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Name your playbook</h3>
              <input
                type="text"
                value={playbookName}
                maxLength={100}
                onChange={(e) => setPlaybookName(e.target.value)}
                placeholder="Enter playbook name"
                className="playbook-name-input"
              />
              <AutoPicksOption checked={autoPicks} onChange={setAutoPicks} />
              <div className="modal-actions">
                <button 
                  className="nav-button back-button" 
                  onClick={() => setShowNameDialog(false)}
                >
                  Cancel
                </button>
                <button
                  className="nav-button next-button"
                  onClick={() => {
                    setShowNameDialog(false);
                    handleSavePlaybook();
                  }}
                  disabled={!playbookName.trim()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  return (
    <div className="simple-builder-section builder-step">
      <div className="step-meta">
        <span>Simple builder</span>
        <span>Step <strong>{currentStep + 1}</strong> of {STRATEGIES.length}</span>
      </div>
      <div className="progress-bar" role="progressbar" aria-valuemin={1} aria-valuemax={STRATEGIES.length} aria-valuenow={currentStep + 1}>
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="strategy-card">
        <h2 className="strategy-name">{currentStrategy.name}</h2>
        <p className="strategy-prompt">{currentStrategy.prompt}</p>
        <div className="options-grid" role="radiogroup" aria-label={currentStrategy.name}>
          {currentStrategy.options.map((option) => (
            <button
              key={option}
              className={`option-button ${selections[currentStrategy.name] === option ? 'selected' : ''}`}
              role="radio"
              aria-checked={selections[currentStrategy.name] === option}
              onClick={() => handleSelection(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className="navigation">
        <button className="nav-button back-button" onClick={handleBack}>
          Back
        </button>
        {currentStep < STRATEGIES.length - 1 ? (
          <button
            className="nav-button next-button"
            onClick={handleNext}
            disabled={!selections[currentStrategy.name]}
          >
            Next
          </button>
        ) : (
          <button
            className="nav-button build-button"
            onClick={handleBuildPlaybook}
            disabled={!selections[currentStrategy.name]}
          >
            Build playbook
          </button>
        )}
      </div>

    </div>
  );
}